import { CalendarClock, CheckCircle2, Lock, PlayCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { useBranding } from '~/helpers/branding/branding'
import { formatSessionDate } from '~/helpers/dates/session-dates'
import { cn } from '~/lib/utils'

export type PathStepView = {
  id: string
  position: number
  unlockAt?: unknown
  unlocked?: boolean | null
  lockReason?: string | null
  module?: { id: string, title?: string | null, status?: string | null } | null
  myTracking?: { completionStatus?: string | null } | null
}

type IProps = {
  steps: PathStepView[]
  onLaunch: (moduleId: string) => void
}

/**
 * Les étapes d'un parcours, avec l'état de chacune.
 *
 * Un module verrouillé reste **visible** et porte la raison de sa fermeture —
 * une date à attendre, ou un module à finir (critère de #182). Le masquer
 * laisserait l'apprenant devant un parcours qui rétrécit sans explication.
 */
function PathSteps({ steps, onLaunch }: IProps) {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()

  return (
    <ul className="flex flex-col gap-1.5">
      {steps.map((step) => {
        const done = step.myTracking?.completionStatus === 'COMPLETED'
        const started = step.myTracking != null && !done
        const unlocked = step.unlocked === true
        const ready = step.module?.status === 'READY'
        const title = step.module?.title ?? ''

        const Icon = done ? CheckCircle2 : unlocked ? PlayCircle : step.lockReason === 'SCHEDULED' ? CalendarClock : Lock

        return (
          <li
            key={step.id}
            className={cn(
              'flex items-center gap-2.5 rounded-md border border-border p-2.5',
              !unlocked && 'bg-muted/40',
            )}
          >
            <Icon
              className={cn(
                'size-4 shrink-0',
                done ? 'text-green-500' : unlocked ? 'text-foreground' : 'text-muted-foreground',
              )}
            />
            <span className="w-5 shrink-0 text-xs tabular-nums text-muted-foreground">
              {step.position + 1}
            </span>

            <div className="min-w-0 flex-1">
              <p className={cn('truncate text-sm', !unlocked && 'text-muted-foreground')}>{title}</p>
              {!unlocked && (
                <p className="truncate text-xs text-muted-foreground">
                  {step.lockReason === 'SCHEDULED' && step.unlockAt
                    ? t('home.path.opensOn', { date: formatSessionDate(step.unlockAt, i18n.language, timezone) })
                    : t('home.path.awaitingPrevious')}
                </p>
              )}
            </div>

            {unlocked && step.module?.id && (
              <Button
                size="sm"
                variant={done ? 'outline' : 'default'}
                disabled={!ready}
                onClick={() => onLaunch(step.module!.id)}
              >
                {done ? t('home.review') : started ? t('home.resume') : t('sessions.my.start')}
              </Button>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export default PathSteps
